import { FC, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import styled from "@emotion/styled";
import { Text } from "@radix-ui/themes";

import { api } from "@/modules/api";
import { atlasRoutes } from "@/app/router/routes";
import { ImportSessionsTable } from "@/pages/imports/shared/import-sessions-table";
import {
  ImportStatus,
  ImportStatuses,
  ImportStatusLabels,
} from "@/pages/imports/shared/import-status";
import { Button } from "@/components/button";

interface ImportSessionSchema {
  id: string;
  owned_by_space_account_id: string;
  title?: string | null;
  status?: string | null;
  kind?: string | null;
  internal_created_at?: string | null;
}

export const ImportsRootPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const owner = searchParams.get("owner") ?? "";

  const pageSize = 100;

  const [statusFilter, setStatusFilter] = useState<ImportStatus | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["import-sessions", statusFilter, owner],
    queryFn: () =>
      api.post("/internal/v1/import-sessions/search", {
        body: {
          filter_by_kind: "IMPORT",
          filter_by_space_account_id: owner || undefined,
          filter_by_status: statusFilter,
          limit: pageSize,
        },
      }),
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  const { data: ownerSpaceAccount } = useQuery({
    queryKey: ["space-account-by-id", owner],
    queryFn: () =>
      api.get("/internal/v1/space-accounts/{space_account_id}", {
        params: { path: { space_account_id: owner } },
      }),
    enabled: !!owner,
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  const tableData = useMemo<ImportSessionSchema[]>(
    () => (data?.data?.results ?? []) as ImportSessionSchema[],
    [data]
  );

  const handleViewImportSession = (id: string) => {
    navigate(atlasRoutes.importsView({ params: { importSessionId: id } }).path);
  };

  return (
    <Container>
      <HeaderRow>
        <h1>Import Sessions</h1>
        <Button variant="solid" onClick={() => navigate(atlasRoutes.importsDashboard({}).path)}>
          View Dashboard
        </Button>
      </HeaderRow>
      {owner && (
        <DescriptionText>
          Filtered to Space Account: {ownerSpaceAccount?.data?.profile_email_address ?? owner}{" "}
          <small>({owner})</small>
        </DescriptionText>
      )}

      <FilterRow>
        {/* Status Filter */}
        <label htmlFor="status-filter">Status:&nbsp;</label>
        <select
          id="status-filter"
          value={statusFilter ?? ""}
          onChange={e =>
            setStatusFilter(e.target.value === "" ? null : (e.target.value as ImportStatus))
          }
        >
          <option value="">All</option>
          {ImportStatuses.map(status => (
            <option key={status} value={status}>
              {ImportStatusLabels[status]}
            </option>
          ))}
        </select>
      </FilterRow>

      {/* Loading and Error States */}
      {isLoading && <div>Loading...</div>}
      {isError && <div>Error fetching import sessions: {error.message}</div>}

      {/* Table or No Results */}
      {tableData.length > 0 ? (
        <ImportSessionsTable
          data={tableData}
          onViewImportSession={handleViewImportSession}
          pageSize={pageSize}
        />
      ) : (
        !isLoading && <div>No import sessions found.</div>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 24px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 16px;

  h1 {
    margin: 0;
  }
`;

const FilterRow = styled.div`
  margin-bottom: 16px;
`;

const DescriptionText = styled(Text)`
  display: block;
  color: var(--gray-12);
  margin-bottom: 16px;
`;
